<script setup lang="ts">
import { ref, computed, type Ref, onBeforeMount, watch, onMounted } from 'vue'
import axios from 'axios'
import type { UiAccordion } from '@afs/design-system'
import { UiAccordionVariant } from '@afs/design-system'

import { defaultAmount } from '~/basics/const'
import {
  amountProps,
  amountSliderProps,
  amountSliderValue,
  durationProps,
  possibleDurations,
  useSimulatorData,
  type formSchemaType,
} from '~/composables/useSimulatorData'
import { useFormValidation } from '~/composables/useFormValidation'
import { VariationMode } from '~/extensions/simulator/simulator.type'
import { useI18n } from 'vue-i18n'
import { callScaleApi, callSimulationApi } from '@/composables/useFinancoApi'
import type { SimulatorPageData } from '../+data'
import { useData } from 'vike-vue/useData'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import { usePageContext } from 'vike-vue/usePageContext'
import { useGtmOnMountedSimulator, useGtmSimulatorChangeStep } from '@/composables/useGtmTag'
import { openLegalNotice, scrollToLegalNotice } from '@/composables/useAccordion'

const { t } = useI18n()

const {
  steps,
  currentStep,
  data,
  formSchema,
  resetForm,
  simulationResponse,
  loading,
  amountByDuration,
  possibleAmounts,
} = useSimulatorData()

const { stepHasBeenSubmitted, getIsStepValid, formatError } = useFormValidation<formSchemaType>(formSchema.value, data)

const { simulator } = useData<SimulatorPageData>()
useFormatSeoContent('Simulateur', 'Simulateur - Description')
useGtmOnMountedSimulator('simulator', data.value.offers.offer, data.value.offers.subOffer, data.value.amount)

const track = useGtmSimulatorChangeStep()

const { urlParsed } = usePageContext()

const offersStep = ref<Ref | null>(null)
const amountStep = ref<Ref | null>(null)
const durationStep = ref<Ref | null>(null)
const apiError = ref<string>('')

const legalNoticeAccordion = ref<InstanceType<typeof UiAccordion> | null>(null)
onMounted(() => openLegalNotice(legalNoticeAccordion.value))

const stepNumber = computed(() => steps.findIndex((step) => step === currentStep.value) + 1)
const progressLabel = computed(() => t('simulator.progressLabel', { current: stepNumber.value, total: steps.length }))

const formStatus = computed(() => ({
  offers: getIsStepValid.value('offers'),
  amount: getIsStepValid.value('amount'),
  duration: getIsStepValid.value('duration'),
  complete: getIsStepValid.value(),
}))

const errors = computed(() => {
  return stepHasBeenSubmitted.value
    ? formatError.value(formStatus.value[currentStep.value as keyof typeof formStatus.value]?.value?.error?.issues)
    : null
})

const isStepBlocked = computed(
  () =>
    stepHasBeenSubmitted.value && !formStatus.value[currentStep.value as keyof typeof formStatus.value].value.success,
)

const stepperNextLabel = computed(() => {
  return currentStep.value === 'duration' ? t('simulator.seeProposals') : t('simulator.next')
})

watch(
  () => stepNumber.value,
  (newValue) => {
    if (newValue) {
      track(
        newValue,
        steps[steps.findIndex((step) => step === currentStep.value)],
        data.value.offers.offer,
        data.value.offers.subOffer,
        data.value.amount,
        data.value.duration[0],
      )
    }
  },
  { immediate: true },
)

const onPreviousStep = () => {
  if (currentStep.value === 'proposals') return

  if (currentStep.value === 'offers') {
    offersStep?.value.container.focus()
  } else if (currentStep.value === 'amount') {
    amountStep?.value.container.focus()
  } else if (currentStep.value === 'duration') {
    if (!formStatus.value.duration.value.success) return
    durationStep?.value.container.focus()
  }

  apiError.value = ''

  const previousIndex = steps.findIndex((step) => step === currentStep.value) - 1

  if (previousIndex >= 0) {
    data.value.timestamp = Date.now()
    currentStep.value = steps[previousIndex]
  }
}

const resetFormBasedOnScale = async () => {
  const [scale] = await callScaleApi(data.value.offers.subOffer)

  durationProps.value = {
    min: scale.numberOfPaymentsMin,
    max: scale.numberOfPaymentsMax,
    step: 6,
  }

  amountProps.value = {
    min: scale.creditAmountMin,
    max: scale.creditAmountMax,
  }

  const medianDuration = (scale.numberOfPaymentsMax + scale.numberOfPaymentsMin) / 2
  possibleDurations.value = []

  for (let i = scale.numberOfPaymentsMin; i <= scale.numberOfPaymentsMax; i = i + durationProps.value.step) {
    possibleDurations.value.push(i)

    // Set default duration value if not specified in the URL
    if (!urlParsed.search.duration && i - medianDuration >= 0 && i - medianDuration < 6) data.value.duration[0] = i
  }

  // Set amount slider value
  amountSliderValue.value = [amountSliderProps.value.max - possibleDurations.value.indexOf(data.value.duration[0])]

  resetForm({
    offer: data.value.offers.offer,
    subOffer: data.value.offers.subOffer,
    amount:
      data.value.amount && (data.value.amount < amountProps.value.min || data.value.amount > amountProps.value.max)
        ? defaultAmount
        : data.value.amount,
    duration: data.value.duration[0] < durationProps.value.min ? [durationProps.value.min] : data.value.duration,
  })

  const formValidation = useFormValidation<formSchemaType>(formSchema.value, data)

  formatError.value = formValidation.formatError.value
  getIsStepValid.value = formValidation.getIsStepValid.value
}

const onOffersStep = async () => {
  offersStep?.value.container.focus()

  loading.value = true

  await resetFormBasedOnScale()

  loading.value = false
}

const onAmountStep = async () => {
  loading.value = true

  const variationsValue = []

  const variationNumbers = (durationProps.value.max - durationProps.value.min) / durationProps.value.step
  for (let i = 1; i <= variationNumbers; i++) {
    variationsValue.push(i * durationProps.value.step)
  }

  const formmatedVariations = variationsValue.map((variationValue) => {
    return {
      identifier: `variation-${VariationMode.duration}-${variationValue}`,
      type: VariationMode.duration,
      duration: variationValue,
    }
  })

  try {
    const allSimulation = await callSimulationApi(
      data.value.amount || 0,
      durationProps.value.min,
      data.value.offers.subOffer,
      formmatedVariations,
    )
    apiError.value = ''

    possibleAmounts.value = [Math.ceil(allSimulation.simulation.monthlyPaymentNoInsurance)]
    if (allSimulation.variations?.length) {
      possibleAmounts.value = possibleAmounts.value
        .concat(allSimulation.variations.map((variation) => Math.ceil(variation.monthlyPaymentNoInsurance)))
        .sort(function (a, b) {
          return b - a
        })
    }

    amountByDuration.value[allSimulation.simulation.numberOfPayments] = Math.ceil(
      allSimulation.simulation.monthlyPaymentNoInsurance,
    )
    if (allSimulation.variations?.length) {
      allSimulation.variations.map(
        (variation) =>
          (amountByDuration.value[variation.numberOfPayments] = Math.ceil(variation.monthlyPaymentNoInsurance)),
      )
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      apiError.value = error?.response?.data?.message || t('simulator.error.default')
    } else {
      apiError.value = t('simulator.error.default')
    }
  } finally {
    loading.value = false
  }
}

const onDurationStep = async () => {
  loading.value = true

  if (data.value.amount && data.value.duration[0] && data.value.offers.subOffer) {
    let variationsValue = [-6, 6]

    if (data.value.duration[0] === durationProps.value.max) {
      variationsValue = [-6]
    }

    if (data.value.duration[0] === durationProps.value.min) {
      variationsValue = [6]
    }

    const formmatedVariations = variationsValue.map((variationValue) => {
      return {
        identifier: `variation-${VariationMode.duration}-${variationValue}`,
        type: VariationMode.duration,
        duration: variationValue,
      }
    })

    try {
      simulationResponse.value = await callSimulationApi(
        data.value.amount,
        data.value.duration[0],
        data.value.offers.subOffer,
        formmatedVariations,
      )
      currentStep.value = 'proposals'
      apiError.value = ''
    } catch (error) {
      if (axios.isAxiosError(error)) {
        apiError.value = error?.response?.data?.message || t('simulator.error.default')
      } else {
        apiError.value = t('simulator.error.default')
      }
    } finally {
      loading.value = false
    }
  }
}

const onNextStep = async () => {
  if (currentStep.value === 'proposals') return

  stepHasBeenSubmitted.value = true

  switch (currentStep.value) {
    case 'offers':
      await onOffersStep()

      if (!formStatus.value.offers.value.success) return

      break
    case 'amount':
      amountStep?.value.container.focus()

      if (!formStatus.value.amount.value.success) return

      await onAmountStep()

      break
    case 'duration':
      durationStep?.value.container.focus()

      if (!formStatus.value.duration.value.success || !formStatus.value.complete.value.success) return
      else if (formStatus.value.complete.value.success) await onDurationStep()

      break
  }

  stepHasBeenSubmitted.value = false

  const nextIndex = steps.findIndex((step) => step === currentStep.value) + 1

  if (nextIndex < steps.length && !simulationResponse.value) {
    data.value.timestamp = Date.now()
    currentStep.value = steps[nextIndex]
  }
}

const legalNotice = {
  id: 'simulator-proposals-legal-notice',
  title: t('simulator.mentionLegal.title'),
}

onBeforeMount(async () => {
  const timestampsDiff = (Date.now() - data.value.timestamp) / 1000

  simulationResponse.value = null

  const searchParams = urlParsed.search

  if (Object.keys(searchParams).length || timestampsDiff > 3600) {
    resetForm({
      duration: searchParams.duration ? [Number(searchParams.duration)] : [],
      offer: searchParams.offer,
      subOffer: searchParams.subOffer,
      amount: searchParams.amount ? Number(searchParams.amount) : defaultAmount,
    })

    if (searchParams.offer && searchParams.subOffer) {
      // On doit récupérer les échelons et mettre à jour le formulaire en conséquence avant de pouvoir continuer
      await resetFormBasedOnScale()

      if (searchParams.amount) {
        // On doit appeler l'API de simulation avant de pouvoir continuer
        await onAmountStep()

        if (searchParams.duration) {
          // On doit appeler l'API de simulation avant de pouvoir continuer
          await onDurationStep()

          // On a toutes les infos dans les paramètres de l'URL, on rédirige directement vers l'étape des propositions
          currentStep.value = steps[3]
          return
        }

        // On a le projet et le montant dans les paramètres de l'URL, on rédirige directement vers l'étape de la durée du crédit
        currentStep.value = steps[2]
        return
      }

      // On a le projet dans les paramètres de l'URL, on rédirige directement vers l'étape du montant
      currentStep.value = steps[1]
      return
    }
  }

  // On n'a pas de paramètre dans l'URL, on rédirige vers la première étape du simulateur
  currentStep.value = steps[0]
})
</script>

<template>
  <div class="Simulator">
    <section class="container">
      <div class="grid">
        <simulator-heading
          class="Simulator__heading mb-6 g-5"
          @simulator-heading::on-legal-button-click="scrollToLegalNotice(legalNoticeAccordion)"
        />
        <p
          v-if="apiError"
          class="Simulator__error mb-6 pa-3 paragraph-3 color-red-01 bg-red-03 radius-s"
        >
          {{ apiError }}
        </p>
        <transition
          name="fade"
          mode="out-in"
        >
          <simulator-proposals
            v-if="simulationResponse"
            class="Simulator__Proposals"
          />
          <molecules-ui-stepper
            v-else-if="currentStep && !simulationResponse"
            :current-step="stepNumber"
            class="Simulator__stepper"
            :total-steps="steps.length"
            :previous-label="$t('simulator.previous')"
            :next-label="stepperNextLabel"
            :progress-offset="0.5"
            :progress-label="progressLabel"
            :step-blocked="isStepBlocked"
            :loading="loading"
            @stepper::previous="onPreviousStep"
            @stepper::next="onNextStep"
          >
            <transition
              name="fade"
              mode="out-in"
            >
              <simulator-step-offers
                v-if="currentStep === 'offers'"
                ref="offersStep"
                key="simulator-step-offers"
                :errors="errors"
              />
              <simulator-step-amount
                v-else-if="currentStep === 'amount'"
                ref="amountStep"
                key="simulator-step-amount"
                :errors="errors"
              />
              <simulator-step-duration
                v-else-if="currentStep === 'duration'"
                ref="durationStep"
                key="simulator-step-duration"
                :errors="errors"
              />
            </transition>
          </molecules-ui-stepper>
        </transition>
      </div>
    </section>
    <template v-if="currentStep === 'proposals'">
      <section
        v-if="simulationResponse?.legalNotice"
        class="container"
      >
        <div class="grid">
          <molecules-ui-accordion
            ref="legalNoticeAccordion"
            class="Simulator__legals my-8"
            :items="[legalNotice]"
          >
            <template
              v-for="item in [legalNotice]"
              :key="item.id"
              #[item.id]
            >
              <p v-html="simulationResponse?.legalNotice" />
            </template>
          </molecules-ui-accordion>
        </div>
      </section>
      <organisms-ui-steps
        v-if="simulator?.stepsBlock"
        class="mb-8"
        v-bind="simulator.stepsBlock"
      />
      <organisms-ui-help
        v-if="simulator?.blockFaq && simulator?.faqList"
        class="mb-9"
        v-bind="simulator.blockFaq"
      >
        <molecules-ui-accordion
          :items="simulator.faqList"
          :variant="UiAccordionVariant.SECONDARY"
          name="simulator-result-faq"
        >
          <template
            v-for="question in simulator.faqList"
            :key="question.id"
            #[question.id]
          >
            <h3
              v-if="question.subtitle"
              class="mb-2 heading-4"
            >
              {{ question.subtitle }}
            </h3>
            <molecules-ui-rich-text :content="question.content" />
          </template>
        </molecules-ui-accordion>
      </organisms-ui-help>
    </template>
  </div>
</template>

<style scoped>
@media (min-width: 768px) {
  .Simulator__heading,
  .Simulator__error {
    grid-column: span 12;
  }
}

@media (min-width: 1024px) {
  .Simulator__heading,
  .Simulator__error {
    grid-column: 2 / span 10;
  }
}

.Simulator__error {
  grid-column: span 4;
  text-align: center;
}

@media (min-width: 768px) {
  .Simulator__error {
    grid-column: span 12;
  }
}

.Simulator__stepper {
  grid-column: span 4;
}

@media (min-width: 768px) {
  .Simulator__stepper {
    grid-column: span 12;
  }
}

@media (min-width: 1024px) {
  .Simulator__stepper {
    grid-column: 3 / span 8;
  }
}

.Simulator__Proposals,
.Simulator__legals {
  grid-column: span 4;
}

@media (min-width: 768px) {
  .Simulator__Proposals,
  .Simulator__legals {
    grid-column: span 12;
  }
}

@media (min-width: 1024px) {
  .Simulator__Proposals,
  .Simulator__legals {
    grid-column: 2 / span 10;
  }
}

.Simulator__legals {
  --scroll-offset: 50px;
}

@media (min-width: 768px) {
  .Simulator__legals {
    --scroll-offset: 200px;
  }
}

.Simulator__legals :deep(.Accordion__item) {
  scroll-margin: var(--scroll-offset);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
